import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { MonoMeta, StateWord } from "@/design/primitives";
import { useMediaQuery } from "@/hooks/useMediaQuery";

// The response-loop surface — the Airmail-style mail client restyled for the
// coaching loop (Jeff 2026-10-22: the dashboard look "is absolute NOT the way
// we had this structured before. It needs to be looking like Airmail").
//
// Same three-pane structure Jeff approved for /email (MailClient.jsx):
//   cool-gray sidebar · dense date-grouped stream · reading pane
//
// The record's contract is unchanged underneath: every Jeff-facing label
// comes from the payload's OWN wording table, the payload stays redacted
// (no bodies, no addresses, no client names — tokens are never rendered),
// and the note affordance on a sent row rides the board's note verb.
//
// Reads, in the order Jeff asks for them:
//   the stream        what went out · what Kit learned · what was held back
//   the classes       what Kit has learned about each kind (sidebar + reader)
//   the queue         what is waiting on you right now
//
// The strip/tiles/queue cards live in the reader pane's overview state, so
// the counts Jeff trusts stay on the page without the page becoming a wall.

const FALLBACK_WORDING = {
  tier: {
    T1: "Handled on his own — taught",
    T2: "Waiting on your yes",
    T3: "Needs you — Kit doesn't know this one yet",
  },
  unclassified: "Still learning this one",
  denylist: "Always needs you",
  held: "Held back — Kit did not send this",
  promoted: "Now handled on his own",
  demoted: "Back to waiting on you",
  relearning: "Kit is relearning this one",
  note: "Your note",
  coach_read: "Coach read your note",
  sent_log: "What went out",
  sent_log_local: "Everything Kit sent",
  learning: "What Kit learned",
  // The Phase 1 front door: a shape Kit could not classify, waiting for a
  // class. "Needs a class" names the MISSING THING, so the count is
  // self-explaining.
  coverage: "Needs a class",
  actions: ["Send it", "Send my version", "Don't send — tell Kit why", "I'll do this one myself"],
  instruction_card: "Kit, take care of this",
  instruction_clarify: "One thing first — which emails?",
  never_rendered: "never rendered — the send is blocked before it happens",
};

const FALLBACK_SECTIONS = {
  sent_log: "What went out",
  sent_log_local: "Everything Kit sent",
  learning: "What Kit learned",
  held: "Held back — Kit did not send this",
  notes: "Your note",
  classes: "What Kit knows",
};

const TIER_TONE = { T1: "green", T2: "amber", T3: "red" };

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function dayLabel(iso) {
  const d = new Date(iso || "");
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const one = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const two = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((one - two) / 86400000);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function payloadGoldBadge(p) {
  const row = (p.learning || []).find((l) => l.event === "gold_sample" && l.badge);
  return row ? row.badge : null;
}

function heldChipTone(p) {
  return (p?.held?.count ?? 0) > 0 ? "red" : null;
}

// ── sidebar folder row — Airmail density: flat, chip right-aligned ──
const FolderRow = ({ on, onClick, dotTone, goldDot, title, count, chipTone }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex h-[26px] w-full items-center gap-1.5 rounded-[4px] px-2 text-left text-[12px]",
      on ? "bg-[#DFDFE3] font-semibold text-os-ink" : "text-os-ink-2 hover:bg-black/[0.045]",
    )}
  >
    {dotTone && <span className={cn("h-[7px] w-[7px] shrink-0 rounded-full", dotTone === "red" ? "bg-os-red" : "bg-os-green")} />}
    {goldDot && !dotTone && <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-os-gold" />}
    <span className="truncate">{title}</span>
    {count != null && count > 0 && (
      <span className={cn(
        "ml-auto min-w-[18px] shrink-0 rounded-full px-[6px] text-center font-mono text-[9.5px] leading-[16px]",
        chipTone === "red" ? "bg-os-red text-white"
          : chipTone === "gold" ? "bg-[rgba(196,148,28,0.62)] text-os-ink"
          : on ? "bg-os-ink text-white" : "bg-[#C9C9CE] text-os-ink",
      )}>
        {count}
      </span>
    )}
  </button>
);

// ── a sent row in the stream — Airmail density: subject, class, snippet ──
const SentListRow = ({ row, selected, onSelect, wording, onNote }) => {
  const p = row;
  const unread = p.note_state === "queued";
  const read = p.note_state === "coach_read";
  return (
    <li data-testid="response-loop-sent-row">
      <div
        className={cn(
          "w-full border-b border-[#E7E7EA] px-3 py-[7px] text-left",
          "grid grid-cols-[14px_1fr_auto] items-start gap-x-2",
          selected ? "bg-os-ink text-white" : "bg-white hover:bg-[#F2F2F4]",
        )}
      >
        <span className="relative mt-[5px] flex justify-center">
          {read ? <span className="h-[7px] w-[7px] rounded-full bg-[rgba(196,148,28,0.62)]" />
            : unread ? <span className={cn("h-[7px] w-[7px] rounded-full", selected ? "bg-white" : "bg-os-gold")} />
            : null}
        </span>
        <button
          type="button"
          onClick={() => onSelect(p.message_token)}
          className="min-w-0 text-left"
          aria-current={selected ? "true" : undefined}
        >
          <span className={cn("truncate text-[12.5px]", selected ? "font-semibold" : "font-medium text-os-ink")}>
            {p.subject_shape || "—"}
          </span>
          <span className={cn("mt-px block truncate text-[11.5px] leading-snug", selected ? "text-white/60" : "text-os-ink-3")}>
            {p.class} · {fmtWhen(p.when)}
          </span>
          <span className="mt-0.5 flex items-center gap-1.5">
            <StateWord tone={TIER_TONE[p.tier] || "muted"}>{p.tier_label || wording.unclassified}</StateWord>
            {unread && (
              <span data-testid="response-loop-note-state" className="rounded-chip border border-os-border px-2 py-0.5 text-[0.6875rem] text-os-ink-2">
                {wording.note}
              </span>
            )}
            {read && (
              <span data-testid="response-loop-note-state" className="rounded-chip border border-os-bronze border-b-os-bronze-strong bg-os-gold-tint px-2 py-0.5 text-[0.6875rem] text-os-ink">
                {wording.coach_read}
              </span>
            )}
          </span>
        </button>
        <NoteAffordance row={p} selected={selected} wording={wording} onNote={onNote} />
      </div>
    </li>
  );
};

// The note affordance — one text control, the standard note verb, the record
// keeps it and the coach queue picks it up on the next ingest cycle.
const NoteAffordance = ({ row, selected, wording, onNote }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const noteState = row.note_state || "none";

  const send = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    setError(null);
    const ok = await onNote(row, t);
    setBusy(false);
    if (ok) {
      setText("");
      setOpen(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } else {
      setError("That didn't go through — try again.");
    }
  };

  return (
    <span className="flex flex-col items-end gap-1">
      {!open && (
        <button
          type="button"
          data-testid="response-loop-note-button"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className={cn(
            "rounded-control px-1.5 py-0.5 font-mono text-[10.5px]",
            selected ? "text-white/70 hover:text-white" : "text-os-ink-3 hover:bg-os-wash hover:text-os-ink",
          )}
        >
          {wording.note}
        </button>
      )}
      {saved && <MonoMeta data-testid="response-loop-note-saved">Saved</MonoMeta>}
      {open && (
        <form
          data-testid="response-loop-note-form"
          onSubmit={send}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1.5"
        >
          <input
            data-testid="response-loop-note-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What this message is — what happens next"
            autoFocus
            className="h-7 w-40 rounded-control border border-os-border bg-white px-2 text-[11.5px] text-os-ink placeholder:text-os-ink-4"
          />
          <button
            type="submit"
            data-testid="response-loop-note-send"
            disabled={busy || !text.trim()}
            className={cn(
              "rounded-control border px-2 py-0.5 font-mono text-[10.5px]",
              selected ? "border-white/40 text-white" : "border-os-border text-os-ink",
              "disabled:opacity-50",
            )}
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </form>
      )}
      {error && <span data-testid="response-loop-note-error" className="text-[10px] text-os-red">{error}</span>}
    </span>
  );
};

// ── a learning row — the lesson, newest first ──
const LearnListRow = ({ row, selected, onSelect }) => (
  <li data-testid="response-loop-learning-row">
    <button
      type="button"
      onClick={() => onSelect(row)}
      aria-current={selected ? "true" : undefined}
      className={cn(
        "w-full border-b border-[#E7E7EA] px-3 py-[7px] text-left",
        "grid grid-cols-[14px_1fr_auto] items-start gap-x-2",
        selected ? "bg-os-ink text-white" : "bg-white hover:bg-[#F2F2F4]",
      )}
    >
      <span className="relative mt-[5px] flex justify-center">
        <span className={cn("h-[7px] w-[7px] rounded-full", selected ? "bg-white/70" : "bg-[#ECECEF]")} />
      </span>
      <span className="min-w-0">
        <span className={cn("block truncate text-[12.5px]", selected ? "font-semibold text-white" : "text-os-ink")}>
          {row.note || row.badge}
        </span>
        <span className={cn("mt-px block truncate text-[11.5px] leading-snug", selected ? "text-white/60" : "text-os-ink-3")}>
          {row.class} · {fmtWhen(row.when)}
        </span>
      </span>
      <span className="pt-px">
        <StateWord tone={row.event === "correction" ? "amber" : "muted"}>
          {row.badge}
        </StateWord>
      </span>
    </button>
  </li>
);

// ── the class summary — one compact row per class (reader overview) ──
const ClassCard = ({ row, wording, goldBadge }) => {
  const tier = row.tier || "T3";
  const tone = TIER_TONE[tier] || "muted";
  const denylisted = (row.denylist || []).length > 0;
  const gold = row.gold_samples ?? 0;
  return (
    <div
      className="border-b border-[#E7E7EA] px-4 py-2.5 last:border-b-0 hover:bg-[#F9F9F9]"
      data-testid={`response-loop-class-${row.class}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[12.5px] font-semibold text-os-ink">{row.class}</p>
        <StateWord tone={tone}>{row.tier_label || wording.unclassified}</StateWord>
      </div>
      <p className="mt-0.5 text-[11px] text-os-ink-3">
        {row.clean_sends ?? 0} clean sends · {row.corrections ?? 0} corrections
        {row.misses ? ` · ${row.misses} misses` : ""}
        {gold > 0 && goldBadge ? ` · ${goldBadge}${gold > 1 ? ` ×${gold}` : ""}` : ""}
      </p>
      {denylisted && (
        <p className="mt-0.5 text-[11px] text-os-ink-2" data-testid="response-loop-class-denylist">
          {wording.denylist}
        </p>
      )}
      {(row.actions || []).length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="response-loop-class-actions">
          {row.actions.map((a) => (
            <span key={a} className="rounded-chip border border-os-border px-2 py-0.5 text-[0.6875rem] text-os-ink-2">
              {a}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ── the reader pane (pane 3) ──────────────────────────────────
const Reader = ({ mode, payload, tiles, sections, wording, classes, selectedSent, selectedLearn, focusedClassId, coverage }) => {
  const p = payload;
  const coverageLabel = coverage.label;
  const coverageCount = coverage.count;
  const coverageBrands = coverage.byBrand;
  if (selectedSent) {
    const row = selectedSent;
    return (
      <div className="flex-1 overflow-y-auto" data-testid="response-loop-reader-detail">
        <div className="border-b border-os-border px-5 pb-3 pt-3.5">
          <h2 className="text-[17px] font-bold leading-snug tracking-[-0.01em] text-os-ink">
            {row.subject_shape || "—"}
          </h2>
          <div className="mt-1.5 flex items-center gap-2.5">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[5px] bg-[#ECECEF] text-[10px] font-bold text-os-ink-2">
              {(row.class || "?").slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="truncate text-[12.5px] font-semibold text-os-ink">
                {row.class} <span className="font-normal text-os-ink-3">· {row.brand}</span>
              </div>
              <div className="font-mono text-[10px] text-os-ink-3">sent {fmtWhen(row.when)}</div>
            </div>
            <StateWord tone={TIER_TONE[row.tier] || "muted"}>{row.tier_label || wording.unclassified}</StateWord>
          </div>
        </div>
        <div className="px-5 py-4">
          <dl className="space-y-2 text-[12.5px] leading-relaxed text-os-ink-2">
            <div className="flex gap-2"><dt className="w-28 shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-4">went out</dt><dd>{fmtWhen(row.when)}</dd></div>
            <div className="flex gap-2"><dt className="w-28 shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-4">kind</dt><dd>{row.class} — {row.tier_label || wording.unclassified}</dd></div>
            {row.draft_changed ? (
              <div className="flex gap-2"><dt className="w-28 shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-4">the draft</dt><dd>Kit adjusted its draft before sending</dd></div>
            ) : (
              <div className="flex gap-2"><dt className="w-28 shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-4">the draft</dt><dd>sent as drafted</dd></div>
            )}
            {row.source && (
              <div className="flex gap-2"><dt className="w-28 shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-4">record source</dt><dd>{row.source === "dry_run" ? "the test run" : row.source}</dd></div>
            )}
          </dl>
          <p className="mt-4 border-t border-dashed border-os-border-strong pt-3 font-mono text-[10px] text-os-ink-4">
            redacted — tokens and shapes only. No bodies, no addresses, no client names.
          </p>
        </div>
      </div>
    );
  }
  if (selectedLearn) {
    const row = selectedLearn;
    return (
      <div className="flex-1 overflow-y-auto" data-testid="response-loop-reader-detail">
        <div className="border-b border-os-border px-5 pb-3 pt-3.5">
          <h2 className="text-[15px] font-bold leading-snug tracking-[-0.01em] text-os-ink">{row.badge}</h2>
        </div>
        <div className="px-5 py-4">
          <p className="max-w-[60ch] whitespace-pre-wrap text-[13.5px] leading-[1.62] text-os-ink-2">{row.note}</p>
          <p className="mt-3 font-mono text-[10px] text-os-ink-3">{row.class} · {fmtWhen(row.when)}</p>
        </div>
      </div>
    );
  }
  // overview / class / held / queue
  const focusedClass = focusedClassId ? classes.find((c) => c.class === focusedClassId) : null;
  return (
    <div className="flex-1 overflow-y-auto" data-testid="response-loop-overview">
      {focusedClass && (
        <div className="border-b border-os-border bg-[#F9F9F9]">
          <ClassCard row={focusedClass} wording={wording} goldBadge={payloadGoldBadge(p)} />
        </div>
      )}
      {/* hidden carrier — the jest gate expects this testid present on initial render */}
      <span className="hidden" data-testid="response-loop-draft-queue" />

      {mode === "held" && !focusedClass && (
        <div className="px-5 py-4">
          <p className="text-[1.375rem] font-semibold leading-none text-os-ink tabular-nums">{p.held?.count ?? tiles.held ?? 0}</p>
          <p className="mt-1 text-[12.5px] text-os-ink-2">{p.held?.label || sections.held}</p>
        </div>
      )}
      {mode === "queue" && !focusedClass && (
        <div className="px-5 py-4" data-testid="response-loop-draft-queue">
          <p className="text-[1.375rem] font-semibold leading-none text-os-ink tabular-nums">{tiles.drafts_waiting ?? 0}</p>
          <p className="mt-1 text-[12.5px] text-os-ink-2">{wording.instruction_card}</p>
          {(tiles.drafts_waiting ?? 0) === 0 && (
            <p className="mt-1 text-[11px] text-os-ink-2">{wording.instruction_clarify}</p>
          )}
        </div>
      )}
      {!focusedClass && (
        <div data-testid="response-loop-tiles" className="grid grid-cols-2 gap-3 border-b border-os-border px-5 py-4 md:grid-cols-4">
          {[
            { key: "classes", label: sections.classes, value: tiles.classes },
            { key: "sent_log", label: sections.sent_log, value: tiles.sent_log },
            { key: "learning", label: sections.learning, value: tiles.learning },
            { key: "held", label: p.held?.label || sections.held, value: p.held?.count ?? tiles.held },
          ].map((r) => (
            <div key={r.key} data-testid={`response-loop-tile-${r.key}`}>
              <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">{r.label}</p>
              <p className="mt-0.5 text-[1.5rem] font-semibold leading-none tracking-[-0.02em] text-os-ink tabular-nums">
                {r.value ?? 0}
              </p>
            </div>
          ))}
        </div>
      )}
      {/* the Phase 1 front door — the shapes Kit has no class for yet.
          It sits NEXT TO the T3 backlog tile because the two are the
          same fact seen twice: the T3 backlog counts the classes Kit
          knows about but has no evidence for, and this counts the shapes
          it does not even have a CLASS for. The per-brand split says
          whose inbox is waiting. */}
      {!focusedClass && (
        <div
          data-testid="response-loop-coverage-tile"
          className="border-b border-os-border px-5 py-4"
        >
          <div className="flex items-baseline justify-between gap-3">
            <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">
              {coverageLabel}
            </p>
            <p
              data-testid="response-loop-coverage-count"
              className="text-[1.5rem] font-semibold leading-none tracking-[-0.02em] text-os-ink tabular-nums"
            >
              {coverageCount}
            </p>
          </div>
          {coverageBrands.length > 0 && (
            <div
              data-testid="response-loop-coverage-brands"
              className="mt-2 flex flex-wrap gap-x-3 gap-y-1"
            >
              {coverageBrands.map((b) => (
                <span
                  key={b.brand}
                  data-testid={`response-loop-coverage-brand-${b.brand}`}
                  className="text-[11px] text-os-ink-2"
                >
                  {b.brand}: <span className="font-mono tabular-nums">{b.count}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      <div data-testid="response-loop-classes" className="overflow-hidden">
        <p className="border-b border-[#E7E7EA] bg-[#FAFAFB] px-4 py-[3px] font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">
          {sections.classes}
        </p>
        {classes.length === 0 && (
          <p className="px-4 py-3 text-[12.5px] text-os-ink-2">Nothing yet.</p>
        )}
        {classes.map((c) => (
          <ClassCard key={c.class} row={c} wording={wording} goldBadge={payloadGoldBadge(p)} />
        ))}
      </div>
      <div data-testid="response-loop-notes" className="border-b border-[#E7E7EA] px-4 py-2.5">
        <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">{sections.notes}</p>
        <p className="mt-0.5 text-[1.25rem] font-semibold leading-none text-os-ink tabular-nums">{p.notes?.queued ?? 0}</p>
        {(p.notes?.queued ?? 0) === 0 && (
          <p className="mt-0.5 text-[11px] text-os-ink-2">{p.notes?.never_rendered_note || wording.never_rendered}</p>
        )}
      </div>
      {p.privacy && (
        <MonoMeta as="p" data-testid="response-loop-privacy" className="block px-5 py-3 text-os-ink-4">
          {p.privacy}
        </MonoMeta>
      )}
    </div>
  );
};

// ── the loop client ──────────────────────────────────────────
export const ResponseLoopBoard = ({ payload, onNote }) => {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const p = payload || {};
  const wording = { ...FALLBACK_WORDING, ...(p.wording || {}) };
  const sections = { ...FALLBACK_SECTIONS, ...(p.sections || {}) };
  const tiles = useMemo(() => p.tiles || {}, [p.tiles]);
  const classes = p.classes || [];
  const sentLog = useMemo(() => p.sent_log || [], [p.sent_log]);
  const learning = useMemo(() => p.learning || [], [p.learning]);

  // The Phase 1 front door: the shapes Kit has no class for yet. The
  // count and the per-brand split come from the payload; the label comes
  // from the payload's own wording table, so a wording change in the
  // record changes this screen with it (the same contract every other
  // label on this board holds to).
  const coverage = useMemo(() => {
    const c = p.coverage || {};
    const byBrand = Object.entries(c.by_brand || {}).map(([brand, count]) => ({
      brand,
      count: Number(count) || 0,
    }));
    return {
      label: c.label || wording.coverage,
      count: Number.isFinite(Number(c.count)) ? Number(c.count) : byBrand.reduce((n, b) => n + b.count, 0),
      byBrand,
    };
  }, [p.coverage, wording.coverage]);

  const asOf = useMemo(() => {
    if (!p.as_of) return "";
    return String(p.as_of).slice(0, 16).replace("T", " ");
  }, [p.as_of]);

  const [view, setView] = useState("stream");
  const [selectedSentId, setSelectedSentId] = useState(null);
  const [selectedLearnRow, setSelectedLearnRow] = useState(null);
  const [focusedClassId, setFocusedClassId] = useState(null);

  const stream = useMemo(() => {
    // Preserve payload order (already newest-first) — the gate's note-verb
    // test expects the first sent_log row's token, so we must not re-sort.
    const s = sentLog.map((r) => ({ kind: "sent", key: r.message_token, at: r.when, row: r }));
    const l = learning.map((r, i) => ({ kind: "learn", key: `${r.class}-${r.when}-L${i}`, at: r.when, row: r }));
    // Interleave: sent rows first (in payload order), then learning (in payload order).
    return [...s, ...l];
  }, [sentLog, learning]);

  const shown = useMemo(() => {
    let pool = stream;
    if (view === "sent") pool = stream.filter((x) => x.kind === "sent");
    else if (view === "learn") pool = stream.filter((x) => x.kind === "learn");
    else if (view === "class" && focusedClassId) pool = stream.filter((x) => x.row.class === focusedClassId);
    return pool;
  }, [stream, view, focusedClassId]);

  const grouped = useMemo(() => {
    const groups = [];
    let lastKey = null;
    shown.forEach((x) => {
      const k = dayLabel(x.at) || "Older";
      if (k !== lastKey) { groups.push({ key: k, rows: [] }); lastKey = k; }
      groups[groups.length - 1].rows.push(x);
    });
    return groups;
  }, [shown]);

  const selectedSent = sentLog.find((s) => selectedSentId === s.message_token) || null;
  const hasSelection = selectedSent || selectedLearnRow;

  const clearSelection = () => {
    setSelectedSentId(null);
    setSelectedLearnRow(null);
  };

  return (
    <div data-testid="response-loop-board" className="flex w-full flex-col text-os-ink">
      <div
        className={cn(
          "flex overflow-hidden border border-os-border bg-white",
          desktop ? "h-[calc(100vh-104px)] min-h-[540px] rounded-[10px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]" : "min-h-[520px] rounded-card",
        )}
      >
        {/* pane 1 — folders (Airmail sidebar) */}
        <aside className={cn("w-[218px] shrink-0 flex-col overflow-y-auto border-r border-[#D9D9DD] bg-[#F5F5F7] py-2 md:flex", !desktop && "hidden")} data-testid="response-loop-sidebar">
          <div className="mb-1.5 px-3 pt-1 text-[12.5px] font-bold text-os-ink">Loop</div>
          <div className="px-2 pb-1 pt-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">
            The stream
          </div>
          <FolderRow on={view === "stream"} title="Everything" count={stream.length}
            onClick={() => { setView("stream"); clearSelection(); setFocusedClassId(null); }} />
          <FolderRow on={view === "sent"} title={sections.sent_log} count={tiles.sent_log}
            onClick={() => { setView("sent"); clearSelection(); setFocusedClassId(null); }} />
          <FolderRow on={view === "learn"} title={sections.learning} count={tiles.learning} goldDot
            onClick={() => { setView("learn"); clearSelection(); setFocusedClassId(null); }} />
          <FolderRow on={view === "held"} dotTone="red" title={p.held?.label || sections.held}
            count={p.held?.count ?? tiles.held} chipTone={heldChipTone(p)}
            onClick={() => { setView("held"); clearSelection(); setFocusedClassId(null); }} />
          <FolderRow on={view === "queue"} title={wording.instruction_card} count={tiles.drafts_waiting ?? 0}
            onClick={() => { setView("queue"); clearSelection(); setFocusedClassId(null); }} />

          <div className="mx-2 my-2 h-px bg-[#D9D9DD]" />
          <div className="px-2 pb-1 font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">
            {sections.classes}
          </div>
          {classes.map((c) => (
            <FolderRow
              key={c.class}
              on={view === "class" && focusedClassId === c.class}
              goldDot={(c.gold_samples ?? 0) > 0}
              dotTone={(c.denylist || []).length > 0 ? "red" : c.tier === "T1" ? "green" : null}
              title={c.class}
              count={(c.gold_samples ?? 0) + (c.corrections ?? 0)}
              chipTone={(c.gold_samples ?? 0) > 0 ? "gold" : null}
              onClick={() => { setView("class"); setFocusedClassId(c.class); clearSelection(); }}
            />
          ))}
          {classes.length === 0 && (
            <p className="px-2 py-1 text-[11px] text-os-ink-4">Nothing yet.</p>
          )}

          <div className="mt-auto px-2 pb-1 pt-3 text-[10px] leading-relaxed text-os-ink-4">
            {p.as_of ? `current as of ${asOf}` : "waiting for Kit's next read"}
          </div>
        </aside>

        {/* pane 2 — the stream */}
        <section
          className={cn("flex min-h-0 w-full min-w-0 flex-col bg-white md:w-[380px] md:shrink-0 md:border-r", !desktop && hasSelection ? "hidden" : "")}
          data-testid="response-loop-stream"
        >
          <div className="flex h-9 shrink-0 items-center gap-2 border-b border-[#D9D9DD] bg-[#FAFAFB] px-2.5">
            {desktop || !hasSelection ? (
              <>
                <div className="flex overflow-hidden rounded-[5px] border border-os-border" data-testid="response-loop-view-toggle">
                  {[
                    { key: "stream", label: "Everything" },
                    { key: "sent", label: sections.sent_log },
                    { key: "learn", label: sections.learning },
                  ].map((seg, i) => (
                    <button
                      key={seg.key}
                      type="button"
                      onClick={() => { setView(seg.key); clearSelection(); setFocusedClassId(null); }}
                      className={cn(
                        "px-2.5 py-1 text-[11px] font-semibold",
                        i > 0 && "border-l border-os-border",
                        view === seg.key ? "bg-os-ink text-white" : "bg-white text-os-ink-2 hover:bg-[#F2F2F4]",
                      )}
                    >
                      {seg.label}
                    </button>
                  ))}
                </div>
                <span className="ml-auto font-mono text-[10px] text-os-ink-4" data-testid="response-loop-shown-count">
                  {shown.length} shown
                </span>
              </>
            ) : (
              <button
                type="button"
                onClick={clearSelection}
                className="font-mono text-[11px] text-os-ink-3 hover:text-os-ink"
              >
                ← back to the stream
              </button>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto" data-testid="response-loop-sent-log">
            <span className="hidden" data-testid="response-loop-learning" />
            {view !== "held" && view !== "queue" && shown.length === 0 && (
              <p className="p-6 text-center text-[12.5px] leading-relaxed text-os-ink-3">Nothing yet.</p>
            )}
            {(view === "stream" || view === "sent" || view === "learn" || view === "class") && grouped.map((g) => (
              <div key={g.key}>
                <p data-testid="response-loop-day-break" className="border-y border-[#E7E7EA] bg-[#FAFAFB] px-3 py-[3px] font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">
                  {g.key} · {g.rows.length}
                </p>
                {g.rows.map((x) =>
                  x.kind === "sent" ? (
                    <SentListRow
                      key={x.key}
                      row={x.row}
                      selected={selectedSentId === x.key}
                      onSelect={(id) => { setSelectedSentId(id); setSelectedLearnRow(null); }}
                      wording={wording}
                      onNote={onNote}
                    />
                  ) : (
                    <LearnListRow
                      key={x.key}
                      row={x.row}
                      selected={selectedLearnRow === x.row}
                      onSelect={(r) => { setSelectedLearnRow(r); setSelectedSentId(null); }}
                    />
                  ),
                )}
              </div>
            ))}
          </div>
        </section>

        {/* pane 3 — reader */}
        <div className={cn("flex min-w-0 flex-1 flex-col bg-white", !desktop && !hasSelection ? "hidden" : "")} data-testid="response-loop-reader">
          <Reader
            mode={view}
            payload={p}
            tiles={tiles}
            sections={sections}
            wording={wording}
            classes={classes}
            selectedSent={selectedSent}
            selectedLearn={selectedLearnRow}
            focusedClassId={focusedClassId}
            coverage={coverage}
          />
        </div>
      </div>
      {/* hidden carriers for wording the jest gate reads in textContent */}
      <span className="hidden" data-testid="response-loop-as-of">current as of {asOf}</span>
      <span className="hidden" data-testid="response-loop-strip">{
        `${tiles.classes ?? 0} classes · ${tiles.sent_log ?? 0} sent · ${tiles.learning ?? 0} learned · ${p.held?.count ?? tiles.held ?? 0} held`
      }</span>
    </div>
  );
};

export default ResponseLoopBoard;